import React, { useState, useEffect } from 'react';
import { XIcon, CheckIcon } from '../../components/Icons';
import { walletOperations } from './WalletOperations';
import { getPrimarySymbol, matchesSymbol, loadTokensXmlAsJson, Chain } from '../../lib/token_loader';
import { Wallet } from './DataTypes';
import './RequestDialog.css';

export interface WalletFilter {
    curves?: string[];
    assets?: string[];
    chains?: string[];
    minBalance?: number;
}

export interface RequestData {
    type: 'CONNECT' | 'TRANSACTION' | 'SIGN_MESSAGE' | 'GET_WALLET_DATA';
    origin: string;
    hostname: string;
    title: string;
    message: string;
    request?: any;
    requestedAddress?: string;
    connectionParams?: {
        address?: string;
        return_private_key?: boolean;
        filter?: WalletFilter;
    };
}

interface RequestDialogProps {
    requestId: string;
    selectedWallet?: any; // Will receive wallet from parent
    wallets: any[]; // All available wallets
    onApprove: (result: any) => void;
    onReject: (reason?: string) => void;
    onClose: () => void;
}

/**
 * Filters wallets based on the provided filter criteria
 * Now supports async token loading for proper symbol alias matching
 */
async function filterWalletsAsync(wallets: Wallet[], filter?: WalletFilter): Promise<any[]> {
    if (!filter || !wallets || wallets.length === 0) {
        return wallets;
    }

    // Load token data if we need to filter by assets (for symbol alias matching)
    let tokenData: Chain[] | null = null;
    if (filter.assets && filter.assets.length > 0) {
        try {
            tokenData = await loadTokensXmlAsJson('tokens.xml');
        } catch (error) {
            console.warn('Failed to load token data for symbol matching:', error);
        }
    }

    return wallets.filter(wallet => {
        // Filter by curve
        if (filter.curves && filter.curves.length > 0) {
            const walletCurve = wallet.curve || 'secp256k1';
            if (!filter.curves.includes(walletCurve)) {
                return false;
            }
        }

        // Filter by chains
        if (filter.chains && filter.chains.length > 0) {
            const walletChains = wallet.chains?.map((c: any) => c.name) || [];
            const hasMatchingChain = filter.chains.some(chain =>
                walletChains.some((wc: string) => wc.toLowerCase() === chain.toLowerCase())
            );
            if (!hasMatchingChain) {
                return false;
            }
        }

        // Filter by assets
        if (filter.assets && filter.assets.length > 0) {
            const walletAssets: string[] = [];

            // Collect all asset symbols from chains
            wallet.chains?.forEach((chain: any) => {
                if (chain.symbol) {
                    walletAssets.push(chain.symbol.toUpperCase());
                }
                // Also check tokens within chains
                chain.tokens?.forEach((token: any) => {
                    if (token.symbol) {
                        walletAssets.push(token.symbol.toUpperCase());
                    }
                });
            });

            // Check if any requested asset matches any wallet asset using symbol aliases
            // This allows "STE" to match wallet with "STR" (both are aliases of "STR/STE")
            const hasMatchingAsset = filter.assets.some(requestedAsset => {
                const requestedUpper = requestedAsset.toUpperCase();
                return walletAssets.some(walletAsset => {
                    // Direct match
                    if (walletAsset === requestedUpper) return true;

                    // If we have token data, check if requested symbol matches any token that has wallet symbol as alias
                    if (tokenData) {
                        // Find tokens that match either the requested or wallet symbol
                        const matchingToken = tokenData.find(token =>
                            matchesSymbol(requestedUpper, token.Symbol) ||
                            matchesSymbol(walletAsset, token.Symbol)
                        );

                        // If we found a token that matches both, they're aliases of each other
                        if (matchingToken) {
                            return matchesSymbol(requestedUpper, matchingToken.Symbol) &&
                                matchesSymbol(walletAsset, matchingToken.Symbol);
                        }
                    }

                    return false;
                });
            });
            if (!hasMatchingAsset) {
                return false;
            }
        }

        // Filter by minimum balance
        if (filter.minBalance !== undefined && filter.minBalance > 0) {
            let totalBalance = 0;

            // Sum up all fiat values
            wallet.chains?.forEach((chain: any) => {
                if (chain.fiatValue) {
                    totalBalance += parseFloat(chain.fiatValue) || 0;
                }
                // Also check tokens within chains
                chain.tokens?.forEach((token: any) => {
                    // If token has a price, calculate its fiat value
                    if (token.price && token.balance) {
                        totalBalance += parseFloat(token.balance) * parseFloat(token.price);
                    }
                });
            });

            if (totalBalance < filter.minBalance) {
                return false;
            }
        }

        return true;
    });
}

export const RequestDialog: React.FC<RequestDialogProps> = ({
    requestId,
    selectedWallet,
    wallets,
    onApprove,
    onReject,
    onClose
}) => {
    const [requestData, setRequestData] = useState<RequestData | null>(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedWalletForRequest, setSelectedWalletForRequest] = useState(selectedWallet);
    const [confirmationPhrase, setConfirmationPhrase] = useState('');
    const [filteredWallets, setFilteredWallets] = useState<any[]>(wallets);

    // Initialize selected wallet for request based on the type and available wallets
    useEffect(() => {
        if (requestData) {
            // Apply filters if provided (now async to support symbol alias matching)
            const filter = requestData.connectionParams?.filter;

            filterWalletsAsync(wallets, filter).then(walletsToConsider => {
                console.log('RequestDialog - Filtering wallets:', {
                    totalWallets: wallets.length,
                    filter: filter,
                    filteredCount: walletsToConsider.length,
                    filteredWallets: walletsToConsider.map((w: any) => ({ id: w.id, name: w.name, curve: w.curve }))
                });
                setFilteredWallets(walletsToConsider);

                if (requestData.type === 'CONNECT' || requestData.type === 'GET_WALLET_DATA') {
                    // If a specific address is requested, find that wallet
                    if (requestData.requestedAddress) {
                        const requestedWallet = walletsToConsider.find((wallet: any) =>
                            wallet.address === requestData.requestedAddress ||
                            wallet.address?.toLowerCase() === requestData.requestedAddress?.toLowerCase()
                        );

                        if (requestedWallet) {
                            setSelectedWalletForRequest(requestedWallet);
                        } else {
                            // Wallet with requested address not found (or doesn't match filters)
                            setSelectedWalletForRequest(null);
                            setError(`Wallet with address ${requestData.requestedAddress} is not loaded in the extension or does not match the requested filters`);
                        }
                    } else {
                        // For CONNECT and GET_WALLET_DATA without specific address, use default selected wallet or first available from filtered list
                        const defaultWallet = walletsToConsider.find((w: any) => w.id === selectedWallet?.id) ||
                            (walletsToConsider.length > 0 ? walletsToConsider[0] : null);
                        setSelectedWalletForRequest(defaultWallet);

                        // Show error if no wallets match the filter
                        if (walletsToConsider.length === 0) {
                            setError('No wallets match the requested filter criteria');
                        }
                    }
                } else {
                    // For TRANSACTION/SIGN_MESSAGE, use selected wallet or first available
                    setSelectedWalletForRequest(selectedWallet || (walletsToConsider.length > 0 ? walletsToConsider[0] : null));
                }
            }).catch(error => {
                console.error('Error filtering wallets:', error);
                // Fallback to unfiltered wallets
                setFilteredWallets(wallets);
            });
        }
    }, [requestData, selectedWallet, wallets]);

    useEffect(() => {
        if (!requestId) return;

        // Fetch request data from background script
        chrome.runtime.sendMessage({
            type: 'GET_PENDING_REQUEST',
            requestId: requestId
        }, (response) => {
            setLoading(false);
            if (response.success) {
                console.log('Request data received:', response.request);
                setRequestData(response.request);
            } else {
                setError(response.error || 'Failed to load request data');
            }
        });
    }, [requestId]);

    const handleApprove = async () => {
        if (!requestData || processing) return;

        console.log('HandleApprove called with requestData:', requestData);

        // Check if private key is requested and phrase is required
        if (requestData.connectionParams?.return_private_key) {
            console.log('Private key requested, checking phrase:', confirmationPhrase);
            if (confirmationPhrase.toLowerCase() !== 'i understand the security risks') {
                setError('You must type the exact phrase to proceed with private key access');
                return;
            }
        }

        setProcessing(true);
        setError(null);

        try {
            let result;
            switch (requestData.type) {
                case 'CONNECT':
                case 'GET_WALLET_DATA':
                    // For CONNECT and GET_WALLET_DATA requests, return wallet data
                    if (!wallets || wallets.length === 0) {
                        throw new Error('No wallets available. Please create a wallet first.');
                    }

                    // Check if a specific address was requested but not found
                    if (requestData.requestedAddress && !selectedWalletForRequest) {
                        throw new Error(`Wallet with address ${requestData.requestedAddress} is not loaded in the extension.`);
                    }

                    // Use selected wallet or first available
                    const walletToReturn = selectedWalletForRequest || wallets[0];

                    // Get accounts data
                    const accounts = walletOperations.getWalletAccounts([walletToReturn]);

                    // Include private key in accounts if requested and confirmed
                    if (requestData.connectionParams?.return_private_key) {
                        console.log('Private key requested, wallet object:', walletToReturn);
                        console.log('Wallet private_key field:', walletToReturn.private_key);

                        // Add private key to each account
                        accounts.forEach(account => {
                            (account as any).privateKey = walletToReturn.private_key;
                        });
                    }

                    // Get assets data
                    const assets = walletToReturn.chains?.flatMap((chain: any) =>
                        [
                            {
                                symbol: chain.symbol,
                                name: chain.name,
                                balance: chain.balance,
                                chain: chain.name,
                                curve: walletToReturn.curve || 'secp256k1'
                            },
                            ...(chain.tokens || []).map((token: any) => ({
                                symbol: token.symbol,
                                name: token.name,
                                balance: token.balance,
                                chain: chain.name,
                                curve: walletToReturn.curve || 'secp256k1'
                            }))
                        ]
                    ) || [];

                    result = {
                        success: true,
                        accounts: accounts,
                        walletAddress: walletToReturn.address, // NEW: Include wallet address
                        walletData: {
                            accounts: accounts,
                            assets: assets
                        }
                    };
                    break;

                case 'TRANSACTION':
                    if (!selectedWalletForRequest) {
                        throw new Error('No wallet selected for transaction.');
                    }

                    // Perform actual transaction signing
                    const txHash = await walletOperations.signTransaction(selectedWalletForRequest, requestData.request);

                    result = {
                        success: true,
                        txHash: txHash
                    };
                    break;

                case 'SIGN_MESSAGE':
                    if (!selectedWalletForRequest) {
                        throw new Error('No wallet selected for signing.');
                    }

                    // Perform actual message signing
                    const signature = await walletOperations.signMessage(selectedWalletForRequest, requestData.request.message);

                    result = {
                        success: true,
                        signature: signature
                    };
                    break;

                default:
                    throw new Error('Unknown request type');
            }

            // Send approval to background script
            chrome.runtime.sendMessage({
                type: 'RESOLVE_REQUEST',
                requestId: requestId,
                result: result
            }, () => {
                onApprove(result);
            });

        } catch (error) {
            console.error('Error processing request:', error);
            setError(error instanceof Error ? error.message : 'Unknown error occurred');
            setProcessing(false);
        }
    };

    const handleReject = () => {
        // Send rejection to background script
        chrome.runtime.sendMessage({
            type: 'REJECT_REQUEST',
            requestId: requestId,
            reason: 'User rejected the request'
        }, () => {
            onReject('User rejected the request');
        });
    };

    if (loading) {
        return (
            <div className="request-dialog-overlay">
                <div className="request-dialog">
                    <div className="request-loading">
                        <div className="spinner"></div>
                        <p>Loading request...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="request-dialog-overlay">
                <div className="request-dialog">
                    <div className="request-error">
                        <p>Error: {error}</p>
                        <button onClick={onClose} className="btn btn-secondary">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!requestData) {
        return null;
    }

    // Special handling for CONNECT requests when no wallet is available
    if ((requestData.type === 'CONNECT' || requestData.type === 'GET_WALLET_DATA') && !selectedWallet) {
        return (
            <div className="request-dialog-overlay">
                <div className="request-dialog">
                    <div className="request-header">
                        <h3>No Wallet Available</h3>
                        <button onClick={onClose} className="close-btn">
                            <XIcon />
                        </button>
                    </div>

                    <div className="request-content">
                        <div className="request-site">
                            <div className="site-icon">🌐</div>
                            <div className="site-info">
                                <div className="site-name">{requestData.hostname}</div>
                                <div className="site-origin">{requestData.origin}</div>
                            </div>
                        </div>

                        <div className="request-message">
                            <p>You need to create a wallet before you can connect to this site.</p>
                        </div>
                    </div>

                    <div className="request-actions">
                        <button onClick={handleReject} className="btn btn-secondary">
                            <XIcon />
                            Cancel
                        </button>
                        <button onClick={() => {
                            // Close request dialog and don't close popup - let user create wallet
                            onClose();
                        }} className="btn btn-primary">
                            Create Wallet
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="request-dialog-overlay">
            <div className="request-dialog">
                <div className="request-header">
                    <h3>{requestData.title}</h3>
                    <button onClick={onClose} className="close-btn">
                        <XIcon />
                    </button>
                </div>

                <div className="request-content">
                    <div className="request-site">
                        <div className="site-icon">
                            <img
                                src={`https://www.google.com/s2/favicons?domain=${requestData.hostname}&sz=32`}
                                alt="Site favicon"
                                onError={(e) => {
                                    // Fallback to emoji if favicon fails to load
                                    const target = e.target as HTMLImageElement;
                                    const parent = target.parentElement!;
                                    parent.removeChild(target);
                                    parent.classList.add('fallback');
                                    parent.textContent = '🌐';
                                }}
                            />
                        </div>
                        <div className="site-info">
                            <div className="site-name">{requestData.hostname}</div>
                            <div className="site-origin">{requestData.origin}</div>
                        </div>
                    </div>

                    <div className="request-message">
                        <p>{requestData.message}</p>
                    </div>

                    {/* Private Key Warning */}
                    {requestData.connectionParams?.return_private_key && (
                        <div className="private-key-warning">
                            <div className="warning-header">
                                <span className="warning-icon">⚠️</span>
                                <h4>SECURITY WARNING</h4>
                            </div>
                            <div className="warning-message">
                                <p><strong>This website is requesting access to your private key!</strong></p>
                                <p>⚠️ Never share your private key with untrusted websites</p>
                                <p>⚠️ This grants FULL access to your wallet</p>
                                <p>⚠️ Only proceed if you absolutely trust this website</p>
                            </div>
                            <div className="confirmation-input">
                                <label>Type the following phrase to confirm:</label>
                                <div className="phrase-to-type">"I understand the security risks"</div>
                                <input
                                    type="text"
                                    value={confirmationPhrase}
                                    onChange={(e) => setConfirmationPhrase(e.target.value)}
                                    placeholder="Type the exact phrase above"
                                    className="confirmation-phrase-input"
                                />
                            </div>
                        </div>
                    )}

                    {/* Wallet Selection for TRANSACTION and SIGN_MESSAGE */}
                    {(requestData.type === 'TRANSACTION' || requestData.type === 'SIGN_MESSAGE') && filteredWallets.length > 1 && (
                        <div className="wallet-selection">
                            <div className="detail-row">
                                <span className="label">Wallet:</span>
                                <select
                                    value={selectedWalletForRequest?.id || ''}
                                    onChange={(e) => {
                                        const wallet = filteredWallets.find(w => w.id === e.target.value);
                                        setSelectedWalletForRequest(wallet);
                                    }}
                                    className="wallet-selector"
                                >
                                    {!selectedWalletForRequest && <option value="">Select a wallet...</option>}
                                    {filteredWallets.map(wallet => (
                                        <option key={wallet.id} value={wallet.id}>
                                            {wallet.name || `Wallet ${wallet.id}`} ({wallet.address?.substring(0, 8)}...)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* CONNECT request shows all available wallets or specific wallet if requested */}
                    {requestData.type === 'CONNECT' && filteredWallets.length > 0 && !requestData.requestedAddress && (() => {
                        console.log('Rendering wallet list:', {
                            type: requestData.type,
                            filteredWalletsLength: filteredWallets.length,
                            requestedAddress: requestData.requestedAddress,
                            filteredWallets: filteredWallets.map(w => ({ id: w.id, name: w.name }))
                        });
                        return (
                            <div className="connect-wallets">
                                <h4>
                                    {filteredWallets.length > 1 ? 'Select Wallet to Connect' : 'Wallet to Connect'}
                                    {requestData.connectionParams?.filter ? ' (Filtered)' : ''}:
                                </h4>
                                {requestData.connectionParams?.filter && (
                                    <div className="filter-info" style={{
                                        background: '#2a2a2a',
                                        border: '1px solid #3a3a3a',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        marginBottom: '12px',
                                        fontSize: '12px',
                                        color: '#9ca3af'
                                    }}>
                                        <strong>Active Filters:</strong>
                                        {requestData.connectionParams.filter.curves && (
                                            <div>• Curves: {requestData.connectionParams.filter.curves.join(', ')}</div>
                                        )}
                                        {requestData.connectionParams.filter.assets && (
                                            <div>• Assets: {requestData.connectionParams.filter.assets.join(', ')}</div>
                                        )}
                                        {requestData.connectionParams.filter.chains && (
                                            <div>• Chains: {requestData.connectionParams.filter.chains.join(', ')}</div>
                                        )}
                                        {requestData.connectionParams.filter.minBalance && (
                                            <div>• Min Balance: ${requestData.connectionParams.filter.minBalance}</div>
                                        )}
                                        <div style={{ marginTop: '8px', color: '#10b981', fontWeight: 500 }}>
                                            ✓ {filteredWallets.length} wallet{filteredWallets.length !== 1 ? 's' : ''} match{filteredWallets.length === 1 ? 'es' : ''} your criteria
                                        </div>
                                    </div>
                                )}
                                <div className="wallet-list">
                                    {filteredWallets.map(wallet => (
                                        <div
                                            key={wallet.id}
                                            className={`wallet-item ${selectedWalletForRequest?.id === wallet.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedWalletForRequest(wallet)}
                                        >
                                            <div className="wallet-checkbox">
                                                <input
                                                    type="radio"
                                                    name="connect-wallet"
                                                    checked={selectedWalletForRequest?.id === wallet.id}
                                                    onChange={() => setSelectedWalletForRequest(wallet)}
                                                />
                                            </div>
                                            <div className="wallet-info">
                                                <div className="wallet-name">{wallet.name || `Wallet ${wallet.id}`}</div>
                                                <div className="wallet-address">{wallet.address}</div>
                                                <div className="wallet-curve">{wallet.curve || 'secp256k1'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* CONNECT request with specific address shows only that wallet */}
                    {requestData.type === 'CONNECT' && requestData.requestedAddress && selectedWalletForRequest && (
                        <div className="connect-wallets">
                            <h4>Connecting with specific wallet:</h4>
                            <div className="wallet-list">
                                <div className="wallet-item selected">
                                    <div className="wallet-checkbox">
                                        <input
                                            type="radio"
                                            name="connect-wallet"
                                            checked={true}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="wallet-info">
                                        <div className="wallet-name">{selectedWalletForRequest.name || `Wallet ${selectedWalletForRequest.id}`}</div>
                                        <div className="wallet-address">{selectedWalletForRequest.address}</div>
                                        <div className="wallet-curve">{selectedWalletForRequest.curve || 'secp256k1'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* GET_WALLET_DATA request shows current wallet info */}
                    {requestData.type === 'GET_WALLET_DATA' && selectedWalletForRequest && (
                        <div className="wallet-data-info">
                            <h4>Wallet Information:</h4>
                            <div className="wallet-info">
                                <div className="wallet-name">{selectedWalletForRequest.name || `Wallet ${selectedWalletForRequest.id}`}</div>
                                <div className="wallet-address">{selectedWalletForRequest.address}</div>
                                <div className="wallet-curve">{selectedWalletForRequest.curve || 'secp256k1'}</div>
                            </div>
                        </div>
                    )}

                    {requestData.type === 'TRANSACTION' && requestData.request && (
                        <div className="transaction-details">
                            <div className="detail-row">
                                <span className="label">To:</span>
                                <span className="value">{requestData.request.to}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Amount:</span>
                                <span className="value">{requestData.request.amount} {requestData.request.asset}</span>
                            </div>
                            {requestData.request.memo && (
                                <div className="detail-row">
                                    <span className="label">Memo:</span>
                                    <span className="value">{requestData.request.memo}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {requestData.type === 'SIGN_MESSAGE' && requestData.request && (
                        <div className="message-details">
                            <div className="detail-row">
                                <span className="label">Message:</span>
                                <span className="value message-text">{requestData.request.message}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="request-actions">
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}
                    <div className="request-buttons">
                        <button onClick={handleReject} className="btn btn-secondary" disabled={processing}>
                            <XIcon />
                            Reject
                        </button>
                        <button onClick={handleApprove} className="btn btn-primary" disabled={
                            processing ||
                            (!!requestData.requestedAddress && !selectedWalletForRequest) ||
                            (requestData.connectionParams?.return_private_key && confirmationPhrase.toLowerCase() !== 'i understand the security risks')
                        }>
                            {processing ? (
                                <>
                                    <div className="spinner-small"></div>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <CheckIcon />
                                    Approve
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
